<template>
  <div class="forum_search">
    <van-search
      v-model="keyword"
      show-action
      placeholder="搜索帖子标题"
      @search="onSearch()"
      @cancel="onCancel"
    />

    <div v-if="showHistory" class="search_history">
      <div class="search_history__header">
        <span class="search_history__title">搜索历史</span>
        <span v-if="history.length" class="search_history__clear" @click="onClearHistory">清空</span>
      </div>
      <div v-if="history.length" class="search_history__list">
        <div v-for="item in history" :key="item" class="search_history__item" @click="onSearch(item)">
          <svg
            class="search_history__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <span class="search_history__text text_ellipsis">{{ item }}</span>
        </div>
      </div>
      <div v-else class="search_history__empty">暂无搜索记录</div>
    </div>

    <div v-else class="forum_search__result">
      <div v-if="loading" class="forum_search__loading">加载中…</div>
      <PostItem v-for="post in list" :key="post.postsId" :data="post" @praise="onPraise" @collect="onCollect" />
      <EmptyState v-if="!loading && list.length === 0" text="没有找到相关帖子" />
    </div>
  </div>
</template>

<script>
import { Search } from 'vant';
import { collectOrCancelPosts, postSearchList, praiseOrCancelPosts } from '@/api/forum';
import { addSearchHistory, clearSearchHistory, getSearchHistory } from '@/utils/forum/searchHistory';
import { isInteractionOk, toastInteraction } from '@/utils/forum/forumInteraction';
import { applyForumPostInteractionPatches } from '@/utils/forum/postInteractionCache';
import EmptyState from './components/EmptyState.vue';
import PostItem from './components/PostItem.vue';

export default {
  name: 'ForumSearch',
  components: { [Search.name]: Search, EmptyState, PostItem },
  data() {
    return {
      keyword: '',
      confirmedKeyword: '',
      list: [],
      searched: false,
      loading: false,
      history: [],
      bootstrappedGroupId: '',
    };
  },
  computed: {
    groupId() {
      return String(this.$route.query.groupId || '0');
    },
    showHistory() {
      const text = this.keyword.trim();
      return !this.searched || !text || text !== this.confirmedKeyword;
    },
  },
  activated() {
    this.bootstrap();
  },
  created() {
    this.bootstrap();
  },
  methods: {
    bootstrap() {
      if (this.bootstrappedGroupId !== this.groupId) {
        this.keyword = '';
        this.confirmedKeyword = '';
        this.list = [];
        this.searched = false;
        this.loading = false;
      }
      this.bootstrappedGroupId = this.groupId;
      this.refreshHistory();
      this.applyCachedPostInteractions();
    },
    refreshHistory() {
      this.history = getSearchHistory();
    },
    applyCachedPostInteractions() {
      const applied = applyForumPostInteractionPatches([this.list]);
      if (applied) this.list = this.list.slice();
    },
    async onSearch(val) {
      const text = String(val || this.keyword).trim();
      if (!text) return;
      this.keyword = text;
      this.confirmedKeyword = text;
      this.loading = true;
      this.searched = true;
      this.history = addSearchHistory(text);
      try {
        const res = await postSearchList({ groupId: this.groupId, title: text });
        this.list = Array.isArray(res.data) ? res.data : (res.data && (res.data.list || res.data.records)) || [];
        this.applyCachedPostInteractions();
      } finally {
        this.loading = false;
      }
    },
    onClearHistory() {
      clearSearchHistory();
      this.history = [];
    },
    onCancel() {
      this.$router.back();
    },
    async onPraise(post) {
      const res = await praiseOrCancelPosts(post.postsId, post.praise ? 2 : 1).catch((err) => err);
      if (!toastInteraction(res, { successMsg: post.praise ? '已取消点赞' : '已点赞' })) return;
      post.praise = !post.praise;
      post.praiseNum = (post.praiseNum || 0) + (post.praise ? 1 : -1);
    },
    async onCollect(post) {
      const res = await collectOrCancelPosts(post.postsId, post.collect ? 2 : 1).catch((err) => err);
      if (!isInteractionOk(res)) return toastInteraction(res, { fallbackMsg: '操作失败' });
      post.collect = !post.collect;
      post.collectNum = (post.collectNum || 0) + (post.collect ? 1 : -1);
    },
  },
};
</script>

<style scoped>
.forum_search {
  min-height: 100vh;
  background: #f9fafb;
}

.search_history {
  padding: 12px 16px;
  background: #fff;
}

.search_history__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.search_history__title {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.search_history__clear {
  color: #0249ff;
  font-size: 12px;
}

.search_history__list {
  display: block;
}

.search_history__item {
  display: flex;
  align-items: center;
  padding: 6px 0;
}

.search_history__item + .search_history__item {
  margin-top: 2px;
}

.search_history__icon {
  width: 14px;
  height: 14px;
  margin-right: 8px;
  flex-shrink: 0;
  color: #ccc;
}

.search_history__text {
  min-width: 0;
  flex: 1;
  color: #333;
  font-size: 14px;
}

.search_history__empty,
.forum_search__loading {
  color: #999;
  font-size: 12px;
}

.forum_search__result {
  padding-top: 8px;
}

.forum_search__loading {
  padding: 16px 12px;
  text-align: center;
}

::v-deep .van-search {
  padding: 8px 12px;
}
</style>
